import Item from './Item';
import Staff from './Staff';
import Transaction from './Transaction';
import * as AT from '../../components/container/attributes';

export default class TransactionItem extends Item {
  constructor({
    id,
    number,
    designation,
    description = undefined,
    parent = undefined,
    active = true,
    option = undefined,
    itemType = undefined,
    titleLevel = undefined,
    resource = undefined,
    adminType = undefined,
    quantity = undefined,
    unit = undefined,
    discount = undefined,
    taxCode = undefined,
    comment = undefined,
    calculated = false,
    transaction = undefined,
    netPrice = undefined,
    netAmount = undefined,
    grossPrice = undefined,
    grossAmount = undefined,
    salesPrice = undefined,
    salesAmount = undefined,
    cost = undefined,
    sortOrder = undefined,
    account = undefined,
    pricePrecision = undefined,
    warning = undefined,
    xmlData = undefined,
    locked = false,
    unlockedByStaff = undefined,
    unlockedDate = undefined,
  }) {
    super(
      id,
      number,
      designation,
      description,
      parent,
      active,
      option,
      itemType,
      titleLevel,
      resource,
      adminType,
      quantity,
      unit,
      discount,
      taxCode,
      comment,
      calculated,
      sortOrder,
    );
    this.transaction = transaction ? new Transaction(transaction) : null;
    this.netPrice = netPrice;
    this.netAmount = netAmount;
    this.grossPrice = grossPrice;
    this.grossAmount = grossAmount;
    this.salesPrice = salesPrice;
    this.salesAmount = salesAmount;
    this.cost = cost;
    this.account = account;
    this.pricePrecision = pricePrecision;
    this.warning = warning ?? null;
    this.xmlData = xmlData ?? null;
    this.locked = locked;
    this.unlockedByStaff = unlockedByStaff ? new Staff(unlockedByStaff) : null;
    this.unlockedDate = unlockedDate;
  }

  static get fkName() {
    return 'transaction_item_id';
  }
  static get apiRoute() {
    return 'transaction_item';
  }
  static get detailPageRoute() {
    return 'transactionDetail';
  }
  static get listPageRoute() {
    return 'transactionDetail';
  }
  get fkName() {
    return TransactionItem.fkName;
  }
  get apiRoute() {
    return TransactionItem.apiRoute;
  }
  get listPageRoute() {
    return TransactionItem.listPageRoute;
  }
  get detailPageRoute() {
    return TransactionItem.detailPageRoute;
  }

  static get listDisplayAttrs() {
    return this.getAttributes(
      [
        'itemType',
        'number',
        'designation',
        'unit',
        'adminType',
        'quantity',
        'grossPrice',
        'discount',
        'salesPrice',
        'salesAmount',
        'taxCode',
        'account',
        'comment',
        'cost',
        'option',
      ],
      [
        'titleLevel',
        'resource',
        'description',
        'calculated',
        'sortOrder',
        'pricePrecision',
        'warning',
        'xmlData',
        'locked',
        'unlockedByStaff',
        'unlockedDate',
      ],
    );
  }

  static get purchaseItemsAttributes() {
    return TransactionItem.listDisplayAttrs;
  }

  static get purchaseItemsAttributesWithoutAccount() {
    return TransactionItem.listDisplayAttrs.filter(
      (att) => att?.name !== 'account',
    );
  }

  static get saleItemsAttributes() {
    return TransactionItem.listDisplayAttrs.filter(
      (att) => att?.name !== 'cost',
    );
  }

  static get saleItemAttributesWithoutAccount() {
    return TransactionItem.listDisplayAttrs.filter(
      (att) => !['cost', 'account'].includes(att?.name),
    );
  }

  static get allAttributes() {
    const baseAttributes = Item.allAttributes;
    const unitAttr = baseAttributes.find((attr) => attr.name === 'unit');
    unitAttr.joinPath = ['unit!fk_transaction_item_unit_id_unit'];
    const taxCodeAttr = baseAttributes.find((attr) => attr.name === 'taxCode');
    taxCodeAttr.joinPath = [
      'tax_code!fk_transaction_item_tax_code_id_tax_code',
    ];
    taxCodeAttr.supplementaryJoinColumns = ['country'];
    const resourceAttr = baseAttributes.find(
      (attr) => attr.name === 'resource',
    );
    resourceAttr.joinPath = [
      'resource!fk_transaction_item_resource_id_resource',
    ];
    const resourceClass = resourceAttr.dataObjectClass;
    const joinedDesignations = new Set(resourceClass.joinAttrsKey);
    resourceAttr.supplementaryJoinColumns = resourceClass
      .localeAttributeNames('designation')
      .filter((name) => !joinedDesignations.has(name));

    const customAttributes = [
      new AT.FkTransactionAttribute({
        name: 'transaction',
        joinPath: [
          'transaction!fk_transaction_item_transaction_id_transaction',
        ],
        tableProperties: {
          header: 'hit-base.common.transaction',
        },
      }),
      new AT.DecimalAttribute({
        name: 'netPrice',
        column: 'net_price',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        creationValue: 0.0,
        tableProperties: {
          header: 'hit-app.common.net-price',
          maxWidth: '0.5fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'netAmount',
        decimalPrecision: 2,
        column: 'net_amount',
        readOnly: true,
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.net-amount',
          maxWidth: '0.65fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossPrice',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        column: 'gross_price',
        default: 0.0,
        tableProperties: {
          header: 'hit-app.common.gross-price-short',
          maxWidth: '0.5fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'grossAmount',
        decimalPrecision: 2,
        column: 'gross_amount',
        default: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.gross-amount',
          maxWidth: '0.65fr',
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'salesPrice',
        decimalPrecisionForcedByColumn: 'pricePrecision',
        column: 'sales_price',
        default: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.sales-price-short',
          maxWidth: '0.5fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.DecimalAttribute({
        name: 'salesAmount',
        decimalPrecision: 2,
        column: 'sales_amount',
        default: 0.0,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.sales-amount-short',
          maxWidth: '0.65fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.FkCostItemAttribute({
        name: 'cost',
        column: 'cost_data_id',
        joinPath: ['cost_data!fk_transaction_item_cost_data_id_cost_data'],
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.cost-forwarded',
          maxWidth: '0.65fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.FkAccountAttribute({
        name: 'account',
        column: 'account_id',
        joinPath: ['account!fk_transaction_item_account_id_account'],
        tableProperties: {
          header: 'hit-app.common.account',
          maxWidth: '0.65fr',
          readOnly: true,
          hideInEditionForItemTypes: ['COMMENT', 'TITLE'],
        },
      }),
      new AT.IntegerAttribute({
        name: 'pricePrecision',
        column: 'price_precision',
        tableProperties: {
          header: 'hit-app.common.price-precision',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'warning',
      }),
      new AT.StringAttribute({
        name: 'xmlData',
        column: 'xml_data',
      }),
      new AT.BooleanAttribute({
        name: 'locked',
        column: 'locked',
        tableProperties: {
          header: 'hit-app.transaction.line-locked',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'unlockedByStaff',
        column: 'unlocked_by_staff_id',
        joinPath: ['staff!fk_transaction_item_unlocked_by_staff_id_staff'],
        fkToUpdate: 'unlocked_by_staff_id',
        onlyFetchDesignation: true,
      }),
      new AT.DateTimeAttribute({
        name: 'unlockedDate',
        column: 'unlocked_date',
      }),
    ];
    return baseAttributes.concat(customAttributes);
  }
}
